import React, { ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';

import { statFormatter } from '@nhl/formatter-utilities';
import { SortingState } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { LocalizedAttribute } from '~/core/types';

import { RankAccessor } from '../../enums';
import { StandingsSeason, TeamStandingsRecord } from '../../types';
import { isFeaturedStandingsClub } from '../../utils';

import { GamesRecordType } from './enums';

export const getShootoutRecord = (record: TeamStandingsRecord): ReactNode => (
    <FormattedMessage
        defaultMessage="{shootoutWins}-{shootoutLosses}"
        description="Denotes shootout record composed of shootout wins and shootout losses"
        values={{ shootoutLosses: record.shootoutLosses, shootoutWins: record.shootoutWins }}
    />
);

export const getRecordStringForType = (
    record: TeamStandingsRecord,
    season: StandingsSeason,
    recordType: GamesRecordType
): ReactNode => {
    let wins: number;
    let losses: number;
    let ties: number;
    let ot: number;

    if (recordType === 'Last10Games') {
        wins = record.l10Wins;
        losses = record.l10Losses;
        ties = record.l10Ties;
        ot = record.l10OtLosses;
    } else {
        wins = record[recordType + 'Wins'];
        losses = record[recordType + 'Losses'];
        ties = record[recordType + 'Ties'];
        ot = record[recordType + 'OtLosses'];
    }

    if (
        wins === 0 &&
        losses === 0 &&
        (season.tiesInUse ? ties === 0 : false) &&
        (season.pointForOTlossInUse ? ot === 0 : false)
    ) {
        return <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />;
    }
    if (season.pointForOTlossInUse && season.tiesInUse) {
        return (
            <FormattedMessage
                defaultMessage="{wins}-{losses}-{ot}-{ties}"
                description="Refers to team record, involving wins, losses, shootout/overtime losses, and ties"
                values={{ losses, ties, ot, wins }}
            />
        );
    }
    if (season.pointForOTlossInUse) {
        return (
            <FormattedMessage
                defaultMessage="{wins}-{losses}-{ot}"
                description="Refers to team record, involving wins, losses, and shoutout/overtime losses"
                values={{ losses, ot, wins }}
            />
        );
    }
    if (season.tiesInUse) {
        return (
            <FormattedMessage
                defaultMessage="{wins}-{losses}-{ties}"
                description="Refers to team record, involving wins, losses, and shootout/overtime losses"
                values={{ losses, ties, wins }}
            />
        );
    }
    return (
        <FormattedMessage
            defaultMessage="{wins}-{losses}"
            description="Refers to team record, involving wins and losses"
            values={{ losses, wins }}
        />
    );
};

export const getPointPercentage = (record: TeamStandingsRecord): string => {
    const value: number = record.points / (record.gamesPlayed * 2);
    return statFormatter.displayValueAs('pointPctg')(value);
};

// only display if table is being sorted, descending, by points (with no secondary, tertiary sorts)
const isValidSortForWildcardCutoffDisplay = (sortingState: SortingState): boolean =>
    sortingState.length === 1 && sortingState[0].id === 'points' && sortingState[0].desc;

// determines the placement of the wildcard cutoff line in standings, currently after 2nd wildcard team
// TODO: possibly replace this with info coming from the API, rather than hardcode
const WILDCARD_CUTOFF_TEAM_NUMBER = 2;

const isDisplayingWildcardCutoffOnRow = (
    rankAccessor: RankAccessor,
    sortingState: SortingState,
    wildcardSequence?: number | undefined
): boolean => {
    // don't display if not wildcard section and is not 2nd team in wildcard (hardcoded)
    if (rankAccessor !== RankAccessor.wildcard || wildcardSequence !== WILDCARD_CUTOFF_TEAM_NUMBER) {
        return false;
    }
    return isValidSortForWildcardCutoffDisplay(sortingState);
};

export const getStandingsRowClassName = (
    record: TeamStandingsRecord,
    rankAccessor: RankAccessor,
    sortingState: SortingState
): string => {
    const classNames: Array<string> = [];

    if (isFeaturedStandingsClub(record)) {
        classNames.push('featured-club-standings-row');
    }

    if (isDisplayingWildcardCutoffOnRow(rankAccessor, sortingState, record.wildcardSequence)) {
        classNames.push('wildcard-standings-cutoff-row');
    }

    return classNames.join(' ');
};

export const getTeamSiteUrlFromCommonName = (teamCommonName: LocalizedAttribute): string => {
    // taking default since this is all that will be used for URL
    const { default: defaultCommonName } = teamCommonName;
    const formattedCommonName = defaultCommonName?.replace(/\s/g, '')?.toLowerCase() || '';
    // TODO: this is temporary until Utah has a final team name
    return `https://www.nhl.com/${defaultCommonName.toLowerCase().includes('utah') ? 'utah' : formattedCommonName}`;
};
